/**
 * The Equipment step's Groups view (`equipment-page-landed`, R10–R15; frame 04).
 *
 * The register grouped by what the customer wrote — six *Spin bike* rows are one decision — on the
 * design system's list `Table` with `rowGroups`. Groups and their order are the ONE identify model's
 * (`buildIdentifyModel`, largest first); this view only intersects each group with the rows the
 * page's chip and search allow, and draws it. A group still to identify offers *Not Tracked* in one
 * press, first where its written category suggests it. A group to confirm shows its guess after what
 * was written, and the guess's reason on a second line (`equipment-page-confirm`, R1–R2; frame 05).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, Table, Tag, type TableRowGroup } from '@repo/design-system';
import { useNotTrackedAction, visuallyHidden, type MarkNotTracked } from '@repo/ui';
import { ink, semantic, space, typeScale, weight } from '@repo/design-tokens';
import {
  groupHint,
  registerRowName,
  suggestsNotTracked,
  type IdentifyGroup,
  type IdentifyModel,
} from '@repo/front-end-utils';
import type { TwinAssetResponse } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import { assetCount, equipmentTagLook, guessText } from './equipmentFilter';
import { equipmentRegisterColumns } from './equipmentRegisterColumns';
import { editNameOnF2, useRegisterCellEditor } from './useRegisterCellEditor';
import { SELECTION_BAR_CLEARANCE } from './EquipmentSelectionBar';
import type { EquipmentWriteDeps } from './useEquipmentRowWrites';

export interface EquipmentGroupsViewProps {
  model: Pick<IdentifyModel, 'groups' | 'stateOf' | 'planStateOf'>;
  rows: readonly TwinAssetResponse[];
  /** The rows the chip and the search allow; undefined shows every row. */
  visibleIds: ReadonlySet<number> | undefined;
  /** Bumped by the page each time it lands; re-opens the first group alone. */
  landingKey: number;
  /** Marks these rows Not tracked; resolves with what was written and what was not. */
  onMarkNotTracked?: MarkNotTracked;
  /**
   * The group's other actions — `equipment-page-identify` puts *Identify* here. `rowIds` are the
   * group's rows the chip and search allow, which is what an action on the group acts on.
   */
  renderGroupActions?: (group: IdentifyGroup, rowIds: number[]) => React.ReactNode;
  /**
   * The group a docked card is open on (`equipment-page-identify`, R3, R5): its header wears the
   * selected look, it is kept open, and the table narrows to Name, Product and Plan (frame 06).
   */
  openGroupKey?: string | null;
  /**
   * The selected group to confirm (`equipment-page-confirm`, R6): its header wears the selected
   * look while no card is open, and it is opened when it becomes selected.
   */
  selectedGroupKey?: string | null;
  /** Focusing or clicking anything in a group's header asks for that group to be selected (R6). */
  onSelectGroup?: (key: string) => void;
  /**
   * The page's one *Not Tracked* action (`EquipmentIdentify`), shared with the identify card so
   * one group never has two batches in flight. Absent, the view keeps its own (tests, other hosts).
   */
  notTracked?: {
    mark: (
      group: IdentifyGroup,
      rowIds: number[],
      onLanded?: (origin: Element | null) => void,
    ) => Promise<boolean>;
    busy: ReadonlySet<string>;
  };
  /**
   * The page's ONE selection, shared with the Rows view (`equipment-page-table`, R11): ticking a
   * group's rows and switching to Rows keeps them ticked. Absent, the rows are not selectable.
   */
  selection?: {
    selectedKeys: readonly number[];
    onSelectionChange: (keys: (string | number)[]) => void;
  };
  /**
   * Name and Asset Identifier edited in the cell, as in the Rows view (`equipment-page-table`, R7):
   * the table's writes and the register-joined plan the answers check against. Absent, read-only.
   */
  writes?: EquipmentWriteDeps;
  layout?: FloorPlannerLayout | null;
}

/** What was written, then — on a group to confirm — the guess (frame 05). Takes the free width. */
const Line = styled.span`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${space[2]};
  min-width: 0;
`;

// What was written gives way first: in the narrowed table (a card open) the guess is what is read.
const Hint = styled.span`
  flex: 0 3 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${ink[3]};
  font-size: ${typeScale.caption1.fontSize};
  line-height: ${typeScale.caption1.lineHeight};
`;

/** *→ Hammer Strength · Iso-Lateral Chest Press*, in the header's strong ink (R1). */
const Guess = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${ink[1]};
  font-weight: ${weight.semibold};

  .guess-arrow {
    margin-right: ${space[2]};
    color: ${ink[4]};
  }
`;

/** The guess's reason, in the information ink: why the guess was made, a fact rather than a job. */
const Reason = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  min-width: 0;
  color: ${semantic.status.roles.information.ink};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Actions = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${space[1]};
`;

/** A visible group: the model's group with only the rows the chip and search allow. */
interface ShownGroup {
  group: IdentifyGroup;
  rowIds: number[];
}

export const EquipmentGroupsView: React.FC<EquipmentGroupsViewProps> = ({
  model,
  rows,
  visibleIds,
  landingKey,
  onMarkNotTracked,
  renderGroupActions,
  openGroupKey = null,
  selectedGroupKey = null,
  onSelectGroup,
  notTracked,
  selection,
  writes,
  layout,
}) => {
  const { editCell } = useRegisterCellEditor({ rows, layout, writes });
  const shown = useMemo<ShownGroup[]>(
    () =>
      model.groups
        .map(group => ({
          group,
          rowIds: visibleIds ? group.rowIds.filter(id => visibleIds.has(id)) : group.rowIds,
        }))
        .filter(entry => entry.rowIds.length > 0),
    [model.groups, visibleIds],
  );

  // The first group is open on landing, the rest closed; afterwards open state is the operator's.
  const firstKey = shown[0]?.group.key ?? null;
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(firstKey ? [firstKey] : []));
  useEffect(() => {
    setOpen(new Set(firstKey ? [firstKey] : []));
    // Only a landing re-seeds; a group emptying under the operator must not collapse the others.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingKey]);

  /*
   * The selected group to confirm is opened when the HOST moves the selection to it (landing, or
   * Enter moving on). Not when the operator's own focus or click selected it: that press may be on
   * the group's toggle, and opening the group under it would make the toggle undo itself (review:
   * frontend, correctness) — and every header Tabbed past would open.
   */
  const selectedHere = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedGroupKey) return;
    if (selectedHere.current === selectedGroupKey) {
      selectedHere.current = null;
      return;
    }
    setOpen(current =>
      current.has(selectedGroupKey) ? current : new Set(current).add(selectedGroupKey),
    );
  }, [selectedGroupKey]);

  const toggle = useCallback((key: string) => {
    setOpen(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Whether the view is still on screen, for work that finishes after it has gone.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const own = useNotTrackedAction({
    onMarkNotTracked,
    stateOf: row => model.stateOf(row),
  });
  const { mark, busy } = notTracked ?? own;

  /**
   * After a press lands: focus the next group's toggle, else the panel's title (A2) — but only
   * where focus still is where the press left it (the pressed button, or lost to the body). A press
   * on a slow link can land after the operator has moved on; taking focus back from the search box
   * or another control mid-task is worse than leaving it (review).
   */
  const moveFocusPast = useCallback(
    (groupKey: string, origin: Element | null) => {
      const index = shown.findIndex(entry => entry.group.key === groupKey);
      const nextKey = shown[index + 1]?.group.key;
      window.setTimeout(() => {
        if (!mounted.current) return;
        const active = document.activeElement;
        if (active && active !== document.body && active !== origin) return;
        // By attribute value, not a selector: a group key is JSON and carries quotes.
        const nextRow = nextKey
          ? Array.from(document.querySelectorAll<HTMLElement>('.tl-table-group')).find(
              row => row.dataset.groupKey === nextKey,
            )
          : undefined;
        const nextToggle = nextRow?.querySelector<HTMLButtonElement>('.tl-table-group-toggle');
        (nextToggle ?? document.getElementById('equipment-main-title'))?.focus();
      }, 0);
    },
    [shown],
  );

  // Through a ref: a toast's Try again can land after the list has re-rendered.
  const moveFocusPastRef = useRef(moveFocusPast);
  moveFocusPastRef.current = moveFocusPast;
  const markNotTracked = useCallback(
    async (group: IdentifyGroup, rowIds: number[]) => {
      // The origin is the element focused when each press (or Try again) began.
      await mark(group, rowIds, origin => moveFocusPastRef.current(group.key, origin));
    },
    [mark],
  );

  /** Focus or a click in a group's header selects it (R6) — read from the row's own attribute. */
  const selectFromEvent = useCallback(
    (event: React.SyntheticEvent) => {
      if (!onSelectGroup) return;
      const row = (event.target as Element | null)?.closest?.('.tl-table-group');
      // By attribute value, not a selector: a group key is JSON and carries quotes.
      const key = row?.getAttribute('data-group-key');
      if (!key || key === selectedGroupKey) return;
      // Only a group to confirm can be selected; marking another would skip a later expand.
      if (shown.some(entry => entry.group.key === key && entry.group.state === 'to_confirm')) {
        selectedHere.current = key;
      }
      onSelectGroup(key);
    },
    [onSelectGroup, selectedGroupKey, shown],
  );

  const rowsById = useMemo(() => new Map(rows.map(row => [row.id, row])), [rows]);
  const data = useMemo(
    () =>
      shown.flatMap(entry =>
        entry.rowIds
          .map(id => rowsById.get(id))
          .filter((row): row is TwinAssetResponse => Boolean(row)),
      ),
    [shown, rowsById],
  );

  const narrow = openGroupKey !== null;
  // Editable, never sortable: the groups keep the model's order (`equipment-page-table`, R5, NFR3).
  const columns = useMemo(
    () => equipmentRegisterColumns(model, { narrow, editable: editCell !== undefined, editCell }),
    [model, narrow, editCell],
  );

  const rowGroups = useMemo<TableRowGroup[]>(
    () =>
      shown.map(({ group, rowIds }) => {
        const count = rowIds.length;
        const offerNotTracked = group.state === 'to_identify' && Boolean(onMarkNotTracked);
        const suggested = offerNotTracked && suggestsNotTracked(group.written.category);
        const guess = group.state === 'to_confirm' && group.proposal ? group.proposal : null;
        const reason = guess?.reason?.trim() || null;
        const notTracked = offerNotTracked ? (
          <Button
            key="not-tracked"
            surface="on-glass"
            size="sm"
            variant={suggested ? 'frosted' : 'text'}
            iconLeft={<Icon name="ban" />}
            /* `loading` alone: it already refuses a second press, and a `disabled` button
               would drop the focus it holds to the page body (review, A2). */
            loading={busy.has(group.key)}
            aria-label={`Not Tracked: ${group.label}, ${assetCount(count)}`}
            onClick={() => void markNotTracked(group, rowIds)}
          >
            Not Tracked
          </Button>
        ) : null;
        return {
          key: group.key,
          label: group.label,
          toggleLabel: `${group.label}, ${assetCount(count)}`,
          rowKeys: rowIds,
          // The group a card is open on stays open (frame 06: *The group stays in view*).
          expanded: open.has(group.key) || group.key === openGroupKey,
          // An open card's group wears it; otherwise the group selected to confirm (R6).
          selected:
            openGroupKey !== null ? group.key === openGroupKey : group.key === selectedGroupKey,
          detail: reason ? (
            <Reason>
              <Icon name="wand-magic-sparkles" />
              <span>{reason}</span>
            </Reason>
          ) : undefined,
          onToggle: () => toggle(group.key),
          header: (
            <>
              <Tag surface="on-glass" size="dense" {...equipmentTagLook(group.state)} count={count}>
                {group.label}
              </Tag>
              <Line>
                <Hint>{groupHint(group.written)}</Hint>
                {guess && (
                  <Guess>
                    {/* Frame 05 draws the kit's arrow here (fa-arrow-right in ink 4). */}
                    <Icon name="arrow-right" size="xs" className="guess-arrow" />
                    <span style={visuallyHidden}>guessed </span>
                    {guessText(guess)}
                  </Guess>
                )}
              </Line>
              <Actions>
                {suggested && notTracked}
                {renderGroupActions?.(group, rowIds)}
                {!suggested && notTracked}
              </Actions>
            </>
          ),
        };
      }),
    [
      shown,
      open,
      toggle,
      busy,
      markNotTracked,
      onMarkNotTracked,
      renderGroupActions,
      openGroupKey,
      selectedGroupKey,
    ],
  );

  return (
    // F2 on a focused row edits its Name (R7). `contents`, so the wrapper adds no box.
    <div
      style={{ display: 'contents' }}
      onKeyDown={editNameOnF2}
      onFocusCapture={selectFromEvent}
      onClickCapture={selectFromEvent}
    >
      <Table<TwinAssetResponse>
        look="list"
        columns={columns}
        data={data}
        keyExtractor={row => row.id}
        rowGroups={rowGroups}
        selectable={selection !== undefined}
        selectedKeys={selection?.selectedKeys as number[] | undefined}
        onSelectionChange={selection?.onSelectionChange}
        getRowLabel={registerRowName}
        emptyMessage="No assets match"
        aria-label="Equipment register, grouped by what was written"
      />
      {/* Room for the overlaid selection bar below the last group, kept whether or not it shows. */}
      {selection && (
        <div aria-hidden="true" style={{ flex: 'none', height: SELECTION_BAR_CLEARANCE }} />
      )}
    </div>
  );
};

export default EquipmentGroupsView;
